// harvestTools.js — E8-T07 (D-WIRE): expose the hermes-harvest server tools
// (`memory` = E2 working memory, `session_search` = E5 cross-session recall) to
// the model via the agentLoop `extraTools` seam, exactly like the Hermes
// radarEmitTool. These tools execute in the RENDERER, where apiFetch is already
// authenticated as the signed-in user, and POST to the Worker endpoints
// (/api/memory/working, /api/memory/session-search) — run_command carries no JWT,
// so this is the only authenticated bridge.
//
// GATING (preserves the sprint invariant "flag OFF ⇒ byte-identical tool set"):
// the tool def comes from the server's own /descriptor endpoint, and a tool is
// only built when that descriptor reports `enabled:true` (i.e. the matching
// HERMES_HARVEST_* flag is ON in the Worker env). Flag OFF ⇒ resolveHarvestTools
// returns the tools that ARE on (often []), so the model's tool set is unchanged.
//
// The resolution is memoized per page-load (the flag is a per-deploy Worker env,
// so it cannot change mid-session without a redeploy + reload). Every execute
// returns a STRING for the model (the loop's tool-result contract) and NEVER
// throws.

import { apiFetch as realApiFetch } from './api.js';

const WORKING_DESCRIPTOR_PATH = '/api/memory/working/descriptor';
const SESSION_SEARCH_DESCRIPTOR_PATH = '/api/memory/session-search/descriptor';
const WORKING_PATH = '/api/memory/working';
const SESSION_SEARCH_PATH = '/api/memory/session-search';

/** Wrap a server tool descriptor ({name,description,parameters}) into an OpenAI function def. */
export function toFunctionDef(descriptor) {
  return {
    type: 'function',
    function: {
      name: descriptor.name,
      description: descriptor.description,
      parameters: descriptor.parameters || { type: 'object', properties: {} },
    },
  };
}

/** Compact, model-facing summary of a memory-tool result. Pure. */
export function formatMemoryResult(data) {
  if (!data || typeof data !== 'object') return 'Erro memory: resposta vazia do servidor.';
  if (data.ok) {
    const used = Number.isFinite(data.used) ? ` (${data.used} chars usados)` : '';
    return `Memória "${data.target || 'memory'}" atualizada${used}.`;
  }
  return `Erro memory: ${data.error || 'falha ao gravar'}${data.target ? ` (target=${data.target})` : ''}.`;
}

/** Compact, model-facing summary of a session_search result. Pure. */
export function formatSessionSearch(data) {
  if (!data || typeof data !== 'object') return 'Erro session_search: resposta vazia do servidor.';
  if (!data.ok) return `Erro session_search: ${data.error || 'falha na busca'}.`;
  const results = Array.isArray(data.results) ? data.results : [];
  if (results.length === 0) return `Nenhum resultado (modo=${data.mode || '?'}).`;
  const lines = results.slice(0, 20).map((r) => {
    if (r.role) {
      // scroll mode: a message row
      return `- [${r.role}] ${String(r.content || '').replace(/\s+/g, ' ').slice(0, 160)}`;
    }
    const id = r.sessionId || r.session_id || '?';
    const name = r.name ? ` "${r.name}"` : '';
    const snippet = r.snippet ? ` — ${String(r.snippet).replace(/\s+/g, ' ').slice(0, 120)}` : '';
    return `- ${id}${name}${snippet}`;
  });
  return `session_search (modo=${data.mode}, ${results.length} resultado(s)):\n${lines.join('\n')}`;
}

function makeMemoryExecute(apiFetch) {
  return async function execute(args = {}) {
    try {
      const res = await apiFetch(WORKING_PATH, { method: 'POST', body: args });
      // The route returns 422 (res.ok false) when the op fails, but still carries
      // the structured result in res.data — read data first, fall back to transport error.
      if (res?.data) return formatMemoryResult(res.data);
      return `Erro memory: ${res?.error || 'sem resposta'}.`;
    } catch (e) {
      return `Erro memory: ${e?.message || 'falha na chamada'}.`;
    }
  };
}

function makeSessionSearchExecute(apiFetch) {
  return async function execute(args = {}) {
    try {
      const res = await apiFetch(SESSION_SEARCH_PATH, { method: 'POST', body: args });
      if (res?.data) return formatSessionSearch(res.data);
      return `Erro session_search: ${res?.error || 'sem resposta'}.`;
    } catch (e) {
      return `Erro session_search: ${e?.message || 'falha na chamada'}.`;
    }
  };
}

let _cache = null; // resolved extraTools once we have a definitive (non-network-error) answer

/** Test seam: drop the per-page-load memo so each test resolves fresh. */
export function __resetHarvestToolsCache() {
  _cache = null;
}

/**
 * Resolve the enabled hermes-harvest extra tools for the agentLoop seam.
 * Fetches both /descriptor endpoints; builds a tool only when the server reports
 * enabled:true. Fail-soft: a network error returns [] WITHOUT caching (so the next
 * turn retries); a definitive answer (incl. both-disabled ⇒ []) is memoized.
 * @param {{ apiFetch?: typeof realApiFetch }} [deps]
 * @returns {Promise<Array<{def:object, execute:Function, mutating:boolean}>>}
 */
export async function resolveHarvestTools({ apiFetch = realApiFetch } = {}) {
  if (_cache) return _cache;
  let wm;
  let ss;
  try {
    [wm, ss] = await Promise.all([
      apiFetch(WORKING_DESCRIPTOR_PATH),
      apiFetch(SESSION_SEARCH_DESCRIPTOR_PATH),
    ]);
  } catch {
    return []; // network blip → no harvest tools this turn; do not cache, retry next
  }
  // Both transport-level failures ⇒ indeterminate (API unreachable) — don't cache.
  if (!wm?.ok && !ss?.ok) return [];

  const tools = [];
  if (wm?.ok && wm.data?.enabled && wm.data.descriptor) {
    // `memory` persists durable state ⇒ mutating (gated by 'ask'/read-only like other writes).
    tools.push({ def: toFunctionDef(wm.data.descriptor), execute: makeMemoryExecute(apiFetch), mutating: true });
  }
  if (ss?.ok && ss.data?.enabled && ss.data.descriptor) {
    // `session_search` is read-only recall ⇒ survives read-only/plan mode.
    tools.push({ def: toFunctionDef(ss.data.descriptor), execute: makeSessionSearchExecute(apiFetch), mutating: false });
  }
  _cache = tools;
  return tools;
}
